import React, { useState, useContext, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { AuthContext } from '../../contexts/AuthContext';
import { updateUserProfile } from '../../utils/apiServices';

function Personal({ onFormSubmit, formNames }) {
  const { user, profile, loading, updateProfile } = useContext(AuthContext);
  const navigate = useNavigate();

  const [formData, setFormData] = useState({
    user_name: '',
    email: '',
    document: '',
    identification_type: '',
    birth_date: '',
    gender: '',
    ethnic_group: '',
    disability: '',
    phone_number: '',
    highest_education_level: '',
    diagnostic_type: '',
    data_consent: false,
  });

  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (!loading && !user) {
      navigate('/login');
    }
  }, [loading, user, navigate]);

  useEffect(() => {
    if (profile) {
      const {
        data_consent,
        diagnostic_type,
        identification_type,
        birth_date,
        gender,
        ethnic_group,
        disability,
        phone_number,
        highest_education_level,
      } = profile;

      setFormData(prevFormData => ({
        ...prevFormData,
        user_name: user.full_name || '',
        email: user.email || '',
        document: user.document || '',
        identification_type: identification_type || '',
        birth_date: birth_date || '',
        gender: gender || '',
        ethnic_group: ethnic_group || '',
        disability: disability || '',
        phone_number: phone_number || '',
        highest_education_level: highest_education_level || '',
        diagnostic_type: diagnostic_type || '',
        data_consent: Boolean(data_consent),
      }));
    }
  }, [profile, user]);

  const handleChange = (event) => {
    const { name, value, type, checked } = event.target;
    setFormData(prevFormData => ({
      ...prevFormData,
      [name]: type === 'checkbox' ? checked : value
    }));
    validateField(name, type === 'checkbox' ? checked : value);
  };

  const validateField = (name, value) => {
    let tempErrors = { ...errors };
    const currentDate = new Date();

    switch (name) {
      case 'data_consent':
        tempErrors[name] = value === true ? '' : 'Debe autorizar el tratamiento de datos';
        break;
      case 'phone_number':
        if (!value || (typeof value === 'string' && value.trim() === '')) {
          tempErrors[name] = 'Este campo es obligatorio';
        } else if (!/^[0-9\- ()]+$/.test(value)) {
          tempErrors[name] = 'Solo se permiten caracteres numéricos y símbolos específicos';
        } else {
          tempErrors[name] = '';
        }
        break;
      case 'birth_date':
        if (!value || (typeof value === 'string' && value.trim() === '')) {
          tempErrors[name] = 'Este campo es obligatorio';
        } else {
          try {
            const inputDate = new Date(value);
            if (isNaN(inputDate.getTime())) {
              tempErrors[name] = 'Formato de fecha inválido';
            } else if (inputDate > currentDate) {
              tempErrors[name] = 'La fecha no puede ser futura';
            } else {
              tempErrors[name] = '';
            }
          } catch (error) {
            tempErrors[name] = 'Formato de fecha inválido';
          }
        }
        break;
      default:
        if (typeof value === 'string') {
          tempErrors[name] = value.trim() !== '' ? '' : 'Este campo es obligatorio';
        } else {
          tempErrors[name] = value ? '' : 'Este campo es obligatorio';
        }
        break;
    }
    setErrors(tempErrors);
  };

  const validateForm = () => {
    let tempErrors = {};
    const currentDate = new Date();

    Object.keys(formData).forEach(key => {
      const value = formData[key];
      // Omitir campos solo lectura gestionados por el backend/Context
      if (key === 'user_name' || key === 'email' || key === 'document') {
        return;
      }

      // Validar que todos los campos obligatorios estén completos
      if (key === 'data_consent') {
        if (value !== true) {
          tempErrors[key] = 'Debe autorizar el tratamiento de datos';
        }
        return;
      }

      if (typeof value === 'string') {
        if (value.trim() === '') {
          tempErrors[key] = 'Este campo es obligatorio';
          return;
        }
      } else if (!value) {
        tempErrors[key] = 'Este campo es obligatorio';
        return;
      }

      // Validar campo de teléfono
      if (key === 'phone_number') {
        if (!/^[0-9\- ()]+$/.test(value)) {
          tempErrors[key] = 'Solo se permiten caracteres numéricos y símbolos específicos';
        }
      }

      // Validar campo de fecha
      if (key === 'birth_date') {
        try {
          const inputDate = new Date(value);
          if (isNaN(inputDate.getTime())) {
            tempErrors[key] = 'Formato de fecha inválido';
          } else if (inputDate > currentDate) {
            tempErrors[key] = 'La fecha no puede ser futura';
          }
        } catch (error) {
          tempErrors[key] = 'Formato de fecha inválido';
        }
      }
    });

    setErrors(tempErrors);
    return Object.keys(tempErrors).length === 0;
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    console.log('Form data antes de validar:', formData);
    if (validateForm() && formData.data_consent) {
      try {
        // Construir payload solo con campos de información personal y diagnóstico
        const {
          user_name: _omitUserName,
          email: _omitEmail,
          document: _omitDocument,
          ...rest
        } = formData;

        const payload = { ...rest };

        console.log('Submitting profile payload:', payload);
        console.log('Valores específicos:', {
          diagnostic_type: payload.diagnostic_type,
          birth_date: payload.birth_date,
          phone_number: payload.phone_number
        });
        const updatedProfile = await updateUserProfile(user.token, payload);
        alert('Perfil actualizado con éxito');

        updateProfile(updatedProfile);

        if (onFormSubmit) {
          onFormSubmit({...formData, company_type: formData.diagnostic_type});
        }
      } catch (error) {
        console.error('Error updating profile:', error);
        console.error('Error response:', error.response?.data);
        let errorMessage = 'Ocurrió un error al actualizar el perfil';

        if (error.response && error.response.data) {
          // Mostrar errores específicos de cada campo
          if (typeof error.response.data === 'object') {
            const errors = [];
            for (const [field, messages] of Object.entries(error.response.data)) {
              if (Array.isArray(messages)) {
                errors.push(`${field}: ${messages.join(', ')}`);
              } else {
                errors.push(`${field}: ${messages}`);
              }
            }
            errorMessage = errors.length > 0 ? errors.join('\n') : errorMessage;
          } else if (error.response.data.detail) {
            errorMessage = error.response.data.detail;
          } else if (error.response.data.error) {
            errorMessage = error.response.data.error;
          }
        } else if (error.message) {
          errorMessage = error.message;
        }

        alert(errorMessage);
      }
    } else {
      console.error("Validation errors", errors);
      alert('Por favor, complete todos los campos requeridos y autorice el tratamiento de datos');
    }
  };

  if (loading || !user) {
    return <p>Cargando...</p>;
  }

  return (
    <div
      className="min-h-screen"
      style={{
        background: 'linear-gradient(135deg, #f4f7fc 0%, #e8f0fe 100%)',
        padding: '60px 20px 80px',
      }}
    >
      <div className="w-full mx-auto" style={{ maxWidth: '1200px' }}>
        {/* Header */}
        <div
          className="text-center"
          style={{ marginBottom: '40px', animation: 'fadeInDown 0.6s ease-out' }}
        >
          <h1
            className="text-icetex font-extrabold"
            style={{
              fontSize: 'clamp(24px, 4vw, 36px)',
              marginBottom: '15px',
              textShadow: '0 2px 10px rgba(0, 63, 129, 0.1)',
            }}
          >
            Información Personal
          </h1>
          <p
            className="text-texto-sec font-semibold mx-auto"
            style={{ fontSize: 'clamp(14px, 2vw, 16px)', lineHeight: '1.5', maxWidth: '600px' }}
          >
            Complete los siguientes datos antes de realizar el autodiagnóstico
          </p>
        </div>

        <p
          className="text-center text-texto-sec font-semibold mx-auto"
          style={{
            fontSize: '13px',
            padding: '15px 0 30px',
            backgroundColor: 'rgba(255, 255, 255, 0.7)',
            borderRadius: '15px',
            marginBottom: '30px',
            maxWidth: '400px',
          }}
        >
          Campos marcados con * son obligatorios
        </p>

        <form onSubmit={handleSubmit} noValidate className="w-full mx-auto" style={{ maxWidth: '1200px' }}>

          {/* Information Container */}
          <div
            className="bg-white"
            style={{
              borderRadius: '25px',
              padding: '40px 30px',
              marginBottom: '30px',
              boxShadow: '0 10px 40px rgba(0, 63, 129, 0.1)',
              transition: 'box-shadow 0.3s ease',
              animation: 'slideUp 0.6s ease-out',
            }}
            onMouseEnter={e => e.currentTarget.style.boxShadow = '0 15px 50px rgba(0, 63, 129, 0.15)'}
            onMouseLeave={e => e.currentTarget.style.boxShadow = '0 10px 40px rgba(0, 63, 129, 0.1)'}
          >
            {/* 3-column grid on md+, single column on mobile */}
            <div
              className="grid max-nav:block"
              style={{ gridTemplateColumns: 'repeat(3, 1fr)', gap: '30px' }}
            >

              {/* Columna 1 */}
              <div className="flex-1 p-0" style={{ marginBottom: '20px' }}>
                <div style={{ marginBottom: '25px' }}>
                  <label
                    htmlFor="full_name"
                    className="block font-bold text-texto-oscuro uppercase"
                    style={{ marginBottom: '10px', fontSize: '13px', letterSpacing: '0.3px' }}
                  >
                    Nombre completo *
                  </label>
                  <input
                    type="text"
                    id="full_name"
                    name="full_name"
                    placeholder="Ingresar nombre completo"
                    required
                    className="w-full text-texto-oscuro outline-none"
                    style={{
                      padding: '14px 18px',
                      border: '2px solid #e8f0fe',
                      borderRadius: '12px',
                      fontSize: '14px',
                      backgroundColor: '#e8f0fe',
                      transition: 'all 0.3s ease',
                      cursor: 'not-allowed',
                      opacity: '0.7',
                    }}
                    value={formData.user_name}
                    readOnly
                  />
                </div>

                <div style={{ marginBottom: '25px' }}>
                  <label
                    htmlFor="identification_type"
                    className="block font-bold text-texto-oscuro uppercase"
                    style={{ marginBottom: '10px', fontSize: '13px', letterSpacing: '0.3px' }}
                  >
                    Tipo de documento de identificación * {errors.identification_type && <span style={{ color: '#cc0000', fontSize: '12px', fontWeight: '700', marginLeft: '8px', animation: 'fadeIn 0.3s ease' }}>{errors.identification_type}</span>}
                  </label>
                  <select
                    id="identification_type"
                    name="identification_type"
                    required
                    className="w-full text-texto-oscuro outline-none cursor-pointer"
                    style={{
                      padding: '14px 18px',
                      border: '2px solid #e8f0fe',
                      borderRadius: '12px',
                      fontSize: '14px',
                      backgroundColor: '#f8fafc',
                      transition: 'all 0.3s ease',
                    }}
                    value={formData.identification_type}
                    onChange={handleChange}
                  >
                    <option value="" disabled>Seleccione un tipo de documento</option>
                    <option value="cédula_ciudadanía">Cédula de Ciudadanía</option>
                    <option value="cédula_extranjería">Cédula de Extranjería</option>
                    <option value="pasaporte">Pasaporte</option>
                  </select>
                </div>

                <div style={{ marginBottom: '25px' }}>
                  <label
                    htmlFor="document"
                    className="block font-bold text-texto-oscuro uppercase"
                    style={{ marginBottom: '10px', fontSize: '13px', letterSpacing: '0.3px' }}
                  >
                    Número de identificación *
                  </label>
                  <input
                    type='text'
                    id="document"
                    name="document"
                    placeholder="Ingresar número de identificación"
                    required
                    className="w-full text-texto-oscuro outline-none"
                    style={{
                      padding: '14px 18px',
                      border: '2px solid #e8f0fe',
                      borderRadius: '12px',
                      fontSize: '14px',
                      backgroundColor: '#e8f0fe',
                      transition: 'all 0.3s ease',
                      cursor: 'not-allowed',
                      opacity: '0.7',
                    }}
                    value={formData.document}
                    readOnly
                  />
                </div>

                <div>
                  <label
                    htmlFor="email"
                    className="block font-bold text-texto-oscuro uppercase"
                    style={{ marginBottom: '10px', fontSize: '13px', letterSpacing: '0.3px' }}
                  >
                    Correo electrónico *
                  </label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    placeholder="example@example.com"
                    required
                    className="w-full text-texto-oscuro outline-none"
                    style={{
                      padding: '14px 18px',
                      border: '2px solid #e8f0fe',
                      borderRadius: '12px',
                      fontSize: '14px',
                      backgroundColor: '#e8f0fe',
                      transition: 'all 0.3s ease',
                      cursor: 'not-allowed',
                      opacity: '0.7',
                    }}
                    value={formData.email}
                    readOnly
                  />
                </div>
              </div>

              {/* Columna 2 */}
              <div className="flex-1 p-0" style={{ marginBottom: '20px' }}>
                <div style={{ marginBottom: '25px' }}>
                  <label
                    htmlFor="birth_date"
                    className="block font-bold text-texto-oscuro uppercase"
                    style={{ marginBottom: '10px', fontSize: '13px', letterSpacing: '0.3px' }}
                  >
                    Fecha de nacimiento * {errors.birth_date && <span style={{ color: '#cc0000', fontSize: '12px', fontWeight: '700', marginLeft: '8px', animation: 'fadeIn 0.3s ease' }}>{errors.birth_date}</span>}
                  </label>
                  <input
                    type="date"
                    id="birth_date"
                    name="birth_date"
                    required
                    className="w-full text-texto-oscuro outline-none"
                    style={{
                      padding: '14px 18px',
                      border: '2px solid #e8f0fe',
                      borderRadius: '12px',
                      fontSize: '14px',
                      backgroundColor: '#f8fafc',
                      transition: 'all 0.3s ease',
                    }}
                    value={formData.birth_date}
                    onChange={handleChange}
                  />
                </div>

                <div style={{ marginBottom: '25px' }}>
                  <label
                    htmlFor="gender"
                    className="block font-bold text-texto-oscuro uppercase"
                    style={{ marginBottom: '10px', fontSize: '13px', letterSpacing: '0.3px' }}
                  >
                    Género * {errors.gender && <span style={{ color: '#cc0000', fontSize: '12px', fontWeight: '700', marginLeft: '8px', animation: 'fadeIn 0.3s ease' }}>{errors.gender}</span>}
                  </label>
                  <select
                    id="gender"
                    name="gender"
                    required
                    className="w-full text-texto-oscuro outline-none cursor-pointer"
                    style={{
                      padding: '14px 18px',
                      border: '2px solid #e8f0fe',
                      borderRadius: '12px',
                      fontSize: '14px',
                      backgroundColor: '#f8fafc',
                      transition: 'all 0.3s ease',
                    }}
                    value={formData.gender}
                    onChange={handleChange}
                  >
                    <option value="" disabled>Seleccione género</option>
                    <option value="masculino">Masculino</option>
                    <option value="femenino">Femenino</option>
                    <option value="no_identifico">No me identifico</option>
                  </select>
                </div>

                <div style={{ marginBottom: '25px' }}>
                  <label
                    htmlFor="ethnic_group"
                    className="block font-bold text-texto-oscuro uppercase"
                    style={{ marginBottom: '10px', fontSize: '13px', letterSpacing: '0.3px' }}
                  >
                    Grupo étnico * {errors.ethnic_group && <span style={{ color: '#cc0000', fontSize: '12px', fontWeight: '700', marginLeft: '8px', animation: 'fadeIn 0.3s ease' }}>{errors.ethnic_group}</span>}
                  </label>
                  <select
                    id="ethnic_group"
                    name="ethnic_group"
                    required
                    className="w-full text-texto-oscuro outline-none cursor-pointer"
                    style={{
                      padding: '14px 18px',
                      border: '2px solid #e8f0fe',
                      borderRadius: '12px',
                      fontSize: '14px',
                      backgroundColor: '#f8fafc',
                      transition: 'all 0.3s ease',
                    }}
                    value={formData.ethnic_group}
                    onChange={handleChange}
                  >
                    <option value="" disabled>Seleccione grupo étnico</option>
                    <option value="otro">Otro</option>
                    <option value="afrodescendiente">Afrodescendiente</option>
                    <option value="indígena">Indígena</option>
                    <option value="mestizo_blanco">Mestizo/Blanco</option>
                    <option value="palanquero_san_brasilio">Palanquero de San Basilio</option>
                    <option value="raizal_san_andrés">Raizal del Archipiélago de San Andrés</option>
                  </select>
                </div>

                <div>
                  <label
                    htmlFor="disability"
                    className="block font-bold text-texto-oscuro uppercase"
                    style={{ marginBottom: '10px', fontSize: '13px', letterSpacing: '0.3px' }}
                  >
                    Discapacidad * {errors.disability && <span style={{ color: '#cc0000', fontSize: '12px', fontWeight: '700', marginLeft: '8px', animation: 'fadeIn 0.3s ease' }}>{errors.disability}</span>}
                  </label>
                  <select
                    id="disability"
                    name="disability"
                    required
                    className="w-full text-texto-oscuro outline-none cursor-pointer"
                    style={{
                      padding: '14px 18px',
                      border: '2px solid #e8f0fe',
                      borderRadius: '12px',
                      fontSize: '14px',
                      backgroundColor: '#f8fafc',
                      transition: 'all 0.3s ease',
                    }}
                    value={formData.disability}
                    onChange={handleChange}
                  >
                    <option value="" disabled>Seleccione si tiene alguna discapacidad</option>
                    <option value="cognitiva">Cognitiva</option>
                    <option value="mental">Mental</option>
                    <option value="multiple">Múltiple</option>
                    <option value="sensorial_auditiva">Sensorial Auditiva</option>
                    <option value="sensorial_física">Sensorial Física</option>
                    <option value="sensorial_visual">Sensorial Visual</option>
                    <option value="ninguna">Ninguna</option>
                  </select>
                </div>
              </div>

              {/* Columna 3 */}
              <div className="flex-1 p-0">
                <div style={{ marginBottom: '25px' }}>
                  <label
                    htmlFor="phone_number"
                    className="block font-bold text-texto-oscuro uppercase"
                    style={{ marginBottom: '10px', fontSize: '13px', letterSpacing: '0.3px' }}
                  >
                    Teléfono/celular * {errors.phone_number && <span style={{ color: '#cc0000', fontSize: '12px', fontWeight: '700', marginLeft: '8px', animation: 'fadeIn 0.3s ease' }}>{errors.phone_number}</span>}
                  </label>
                  <input
                    type="tel"
                    id="phone_number"
                    name="phone_number"
                    placeholder="Ingresar número de contacto"
                    required
                    className="w-full text-texto-oscuro outline-none"
                    style={{
                      padding: '14px 18px',
                      border: '2px solid #e8f0fe',
                      borderRadius: '12px',
                      fontSize: '14px',
                      backgroundColor: '#f8fafc',
                      transition: 'all 0.3s ease',
                    }}
                    value={formData.phone_number}
                    onChange={handleChange}
                  />
                </div>

                <div style={{ marginBottom: '25px' }}>
                  <label
                    htmlFor="highest_education_level"
                    className="block font-bold text-texto-oscuro uppercase"
                    style={{ marginBottom: '10px', fontSize: '13px', letterSpacing: '0.3px' }}
                  >
                    Nivel educativo * {errors.highest_education_level && <span style={{ color: '#cc0000', fontSize: '12px', fontWeight: '700', marginLeft: '8px', animation: 'fadeIn 0.3s ease' }}>{errors.highest_education_level}</span>}
                  </label>
                  <select
                    id="highest_education_level"
                    name="highest_education_level"
                    required
                    className="w-full text-texto-oscuro outline-none cursor-pointer"
                    style={{
                      padding: '14px 18px',
                      border: '2px solid #e8f0fe',
                      borderRadius: '12px',
                      fontSize: '14px',
                      backgroundColor: '#f8fafc',
                      transition: 'all 0.3s ease',
                    }}
                    value={formData.highest_education_level}
                    onChange={handleChange}
                  >
                    <option value="" disabled>Seleccione nivel educativo</option>
                    <option value="primaria">Primaria</option>
                    <option value="secundaria">Secundaria</option>
                    <option value="técnico_tecnológico">Técnico o Tecnológico</option>
                    <option value="universitario_pregrado">Universitario (pregrado)</option>
                    <option value="especialización_maestría">Especialización o maestría</option>
                    <option value="doctorado_postdoctorado">Doctorado o postdoctorado</option>
                  </select>
                </div>

                <div>
                  <label
                    htmlFor="diagnostic_type"
                    className="block font-bold text-texto-oscuro uppercase"
                    style={{ marginBottom: '10px', fontSize: '13px', letterSpacing: '0.3px' }}
                  >
                    Tipo de Diagnóstico * {errors.diagnostic_type && <span style={{ color: '#cc0000', fontSize: '12px', fontWeight: '700', marginLeft: '8px', animation: 'fadeIn 0.3s ease' }}>{errors.diagnostic_type}</span>}
                  </label>
                  <select
                    id="diagnostic_type"
                    name="diagnostic_type"
                    required
                    className="w-full text-texto-oscuro outline-none cursor-pointer"
                    style={{
                      padding: '14px 18px',
                      border: '2px solid #e8f0fe',
                      borderRadius: '12px',
                      fontSize: '14px',
                      backgroundColor: '#f8fafc',
                      transition: 'all 0.3s ease',
                    }}
                    value={formData.diagnostic_type}
                    onChange={handleChange}
                  >
                    <option value="" disabled>Seleccione el tipo de diagnóstico</option>
                    {formNames.map((formName) => (
                      <option key={formName} value={formName}>{formName}</option>
                    ))}
                  </select>
                </div>
              </div>
            </div>
          </div>

          {/* Data Treatment */}
          <div
            style={{
              borderRadius: '20px',
              padding: '30px',
              border: '2px solid #b3d9ff',
              marginBottom: '30px',
              background: 'linear-gradient(135deg, #e8f4ff 0%, #d4e9ff 100%)',
              animation: 'slideUp 0.6s ease-out 0.3s both',
            }}
          >
            <p
              className="text-texto-oscuro font-semibold"
              style={{ fontSize: '13px', lineHeight: '1.8', padding: '0 0 20px 0' }}
            >
              Autoriza a ICETEX como responsable del tratamiento de los datos personales,
              para la recolección, almacenamiento, uso, transmisión y/o transferencia de los datos personales
              suministrados en este formulario, para las finalidades dispuestas en la
              política de tratamiento de datos personales.
            </p>
            <div
              className="flex items-center text-texto-oscuro font-semibold"
              style={{ gap: '12px', fontSize: '13px' }}
            >
              <input
                type="checkbox"
                id="data_consent"
                name="data_consent"
                checked={formData.data_consent}
                onChange={handleChange}
                className="cursor-pointer"
                style={{ width: '20px', height: '20px', accentColor: 'var(--color-azul-icetex)' }}
              />
              <label htmlFor="data_consent" className="cursor-pointer select-none" style={{ paddingLeft: '5px' }}>
                Autorizo tratamiento de datos personales.
              </label>
            </div>
            {!formData.data_consent && (
              <span
                className="block"
                style={{
                  color: '#cc0000',
                  marginTop: '15px',
                  fontSize: '13px',
                  fontWeight: '600',
                  padding: '12px 16px',
                  backgroundColor: '#ffeeee',
                  border: '2px solid #ffcccc',
                  borderRadius: '10px',
                  animation: 'shake 0.5s ease',
                }}
              >
                Debe autorizar el tratamiento de los datos personales para continuar.
              </span>
            )}
          </div>

          {/* Submit Button */}
          <div
            className="flex justify-center items-center"
            style={{ animation: 'slideUp 0.6s ease-out 0.4s both' }}
          >
            <button
              type="submit"
              className="text-white border-0 font-bold uppercase cursor-pointer"
              disabled={!formData.data_consent}
              style={{
                background: formData.data_consent
                  ? 'linear-gradient(135deg, var(--color-azul-icetex) 0%, #0056b3 100%)'
                  : '#94a3b8',
                padding: '18px 50px',
                borderRadius: '50px',
                fontSize: '15px',
                letterSpacing: '0.5px',
                boxShadow: formData.data_consent ? '0 6px 20px rgba(0, 63, 129, 0.3)' : 'none',
                marginTop: '20px',
                transition: 'all 0.3s ease',
                opacity: formData.data_consent ? '1' : '0.5',
                cursor: formData.data_consent ? 'pointer' : 'not-allowed',
              }}
            >
              Continuar con el autodiagnóstico
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default Personal;
